import { useMemo } from 'react';
import {
  StructureDefinitionWrapper,
  WrappedStructureDefinition,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/StructureDefinition';
import { useStructureDefinitions } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { getReferenceId } from 'src/utils/fhir';
import { getUUID } from 'src/utils/url';

interface UseFilteredStructureDefinitionsArgs {
  authUser: any;
  allUserOrganizations?: boolean;
  status?: string;
}

export function useStructureDefinitionsFiltered({
  authUser,
  allUserOrganizations = false,
  status,
}: UseFilteredStructureDefinitionsArgs) {
  const [structureDefinition, meta] = useStructureDefinitions({
    filter: {
      'context-type': 'system,organization',
      ...(status ? { status: status } : {}),
    },
    map: StructureDefinitionWrapper,
  });

  const userOrganizations = useMemo(() => {
    if (allUserOrganizations) {
      return authUser?.organizations ?? [];
    }

    return getUUID(authUser?.organization_fhir_uri) ?? [];
  }, [authUser?.organizations, authUser?.organization_fhir_uri, allUserOrganizations]);

  const { structureDefinitionSys, structureDefinitionOrg } = useMemo(
    () =>
      structureDefinition?.reduce(
        (
          result: {
            structureDefinitionSys: WrappedStructureDefinition[];
            structureDefinitionOrg: WrappedStructureDefinition[];
          },
          item
        ) => {
          const useContextOrg = getReferenceId(item?.useContext?.[0]?.valueReference?.reference);

          if (useContextOrg) {
            const orgUser = userOrganizations?.includes(useContextOrg);
            if (orgUser) {
              result.structureDefinitionOrg.push(item);
            }
          } else {
            result.structureDefinitionSys.push(item);
          }

          return result;
        },
        { structureDefinitionSys: [], structureDefinitionOrg: [] }
      ) ?? { structureDefinitionSys: [], structureDefinitionOrg: [] },
    [structureDefinition, userOrganizations]
  );

  return {
    structureDefinitionSys,
    structureDefinitionOrg,
    structureDefinition,
    userOrganizations,
    ...meta,
  };
}
